(() => {
  const PENDING_PACKAGE_KEY = "stars-cars:pending-package:v1";
  const GROUP_PROPERTY = "_stars_cars_group_id";
  const TIER_PROPERTY = "_stars_cars_ticket_tier";
  const PASSENGER_PROPERTY = "_stars_cars_passenger_selection";
  const PENDING_MAX_AGE = 30 * 60 * 1000;

  const shopRoot = () => window.Shopify?.routes?.root || "/";
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  async function fetchJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.headers || {}),
      },
    });
    const text = await response.text();
    let body = null;

    try {
      body = text ? JSON.parse(text) : {};
    } catch (_error) {
      throw new Error(`Cart request returned an unexpected response (${response.status}).`);
    }

    if (!response.ok || body?.errors || body?.status) {
      throw new Error(
        body?.description || body?.message || body?.errors || `Cart request failed (${response.status}).`,
      );
    }

    return body;
  }

  function readPendingPackage() {
    try {
      const pending = JSON.parse(sessionStorage.getItem(PENDING_PACKAGE_KEY) || "null");
      if (!pending?.groupId || !pending.ticketVariantId || !pending.addonVariantId) return null;

      if (!pending.createdAt || Date.now() - pending.createdAt > PENDING_MAX_AGE) {
        sessionStorage.removeItem(PENDING_PACKAGE_KEY);
        return null;
      }

      return pending;
    } catch (_error) {
      try {
        sessionStorage.removeItem(PENDING_PACKAGE_KEY);
      } catch (_storageError) {
        // No action needed.
      }
      return null;
    }
  }

  function clearPendingPackage() {
    try {
      sessionStorage.removeItem(PENDING_PACKAGE_KEY);
    } catch (_error) {
      // The package is already finalized; storage cleanup is best effort.
    }
  }

  function itemProperties(item) {
    return item?.properties && typeof item.properties === "object" ? item.properties : {};
  }

  function isAddonLine(item) {
    return String(itemProperties(item)._et_addon || "") === "true";
  }

  function matchesPackageRole(item, variantId, role) {
    if (String(item.variant_id) !== String(variantId)) return false;
    return role === "addon" ? isAddonLine(item) : !isAddonLine(item);
  }

  function findGroupedLine(cart, pending, role) {
    const variantId = role === "addon" ? pending.addonVariantId : pending.ticketVariantId;
    return (cart.items || []).find(
      (item) =>
        matchesPackageRole(item, variantId, role) &&
        String(itemProperties(item)[GROUP_PROPERTY] || "") === String(pending.groupId),
    );
  }

  function baselineQuantityForKey(pending, key) {
    const baselineItem = (pending.baselineItems || []).find((item) => item.key === key);
    return Number(baselineItem?.quantity || 0);
  }

  function findNewUngroupedUnit(cart, pending, role) {
    const variantId = role === "addon" ? pending.addonVariantId : pending.ticketVariantId;

    return (cart.items || [])
      .filter(
        (item) =>
          matchesPackageRole(item, variantId, role) &&
          !itemProperties(item)[GROUP_PROPERTY],
      )
      .map((item) => {
        const baselineQuantity = baselineQuantityForKey(pending, item.key);
        return {
          item,
          baselineQuantity,
          addedQuantity: Number(item.quantity) - baselineQuantity,
        };
      })
      .filter((candidate) => candidate.addedQuantity > 0)
      .sort((a, b) => b.addedQuantity - a.addedQuantity)[0];
  }

  async function changeLine(key, quantity, properties) {
    const body = { id: key, quantity };
    if (properties) body.properties = properties;

    return fetchJson(`${shopRoot()}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  async function addGroupedLine(item, quantity, properties) {
    return fetchJson(`${shopRoot()}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: Number(item.variant_id),
            quantity,
            properties,
          },
        ],
      }),
    });
  }

  async function attachRoleToPackage(cart, pending, role) {
    if (findGroupedLine(cart, pending, role)) return cart;

    const candidate = findNewUngroupedUnit(cart, pending, role);
    if (!candidate) return null;

    const groupedProperties = {
      ...itemProperties(candidate.item),
      [GROUP_PROPERTY]: pending.groupId,
    };

    if (role === "addon") {
      groupedProperties[PASSENGER_PROPERTY] = pending.passengerTitle || candidate.item.variant_title;
    } else {
      groupedProperties[TIER_PROPERTY] = pending.ticketTitle || candidate.item.variant_title;
    }

    if (candidate.baselineQuantity > 0) {
      await changeLine(candidate.item.key, candidate.baselineQuantity);
      await addGroupedLine(candidate.item, candidate.addedQuantity, groupedProperties);
      return fetchJson(`${shopRoot()}cart.js`);
    }

    return changeLine(candidate.item.key, candidate.item.quantity, groupedProperties);
  }

  async function waitForMiniCart(timeout = 4000) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeout) {
      if (window.miniCart) return window.miniCart;
      await delay(80);
    }
    return null;
  }

  async function refreshCartPresentation(cart) {
    document.dispatchEvent(
      new CustomEvent("stars-cars:package-finalized", {
        bubbles: true,
        detail: { cart },
      }),
    );

    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    const miniCart = await waitForMiniCart();
    if (!miniCart) {
      window.location.reload();
      return;
    }

    if (typeof miniCart.updateSectionContents === "function") {
      // Let Maranello's connectedCallback refresh finish before replacing it
      // with the finalized grouped-cart response.
      await delay(150);
      await miniCart.updateSectionContents();
    }

    const miniCartModal = document.getElementById("HeaderMiniCart");
const cartIcon = miniCartModal?.querySelector(
  ".head-slot-cart-link.dropdown-modal-toggle",
);

// Avoid accidentally closing it if it is already open.
if (miniCartModal?.hasAttribute("open")) return;

if (cartIcon) {
  cartIcon.click();
  return;
}

// Fallback if the cart icon markup ever changes.
if (typeof miniCart.open === "function") {
  miniCart.open();
} else {
  document.dispatchEvent(new CustomEvent("open-mini-cart"));
}
  }

  async function finalizePendingPackage() {
    const pending = readPendingPackage();
    if (!pending) return;

    let cart = null;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      cart = await fetchJson(`${shopRoot()}cart.js`);

      if (!findGroupedLine(cart, pending, "ticket")) {
        const ticketResult = await attachRoleToPackage(cart, pending, "ticket");
        if (ticketResult) cart = ticketResult;
      }

      cart = await fetchJson(`${shopRoot()}cart.js`);

      if (!findGroupedLine(cart, pending, "addon")) {
        const addonResult = await attachRoleToPackage(cart, pending, "addon");
        if (addonResult) cart = addonResult;
      }

      cart = await fetchJson(`${shopRoot()}cart.js`);
      const ticketReady = Boolean(findGroupedLine(cart, pending, "ticket"));
      const addonReady = Boolean(findGroupedLine(cart, pending, "addon"));

      if (ticketReady && addonReady) {
        clearPendingPackage();
        await refreshCartPresentation(cart);
        return;
      }

      await delay(250 * (attempt + 1));
    }

    const partiallyFinalized =
      cart &&
      (findGroupedLine(cart, pending, "ticket") || findGroupedLine(cart, pending, "addon"));

    if (!partiallyFinalized) clearPendingPackage();
    throw new Error("The Stars & Cars package could not be finalized after the Easy Events refresh.");
  }

  if (!window.__starsCarsPackageFinalizerStarted) {
    window.__starsCarsPackageFinalizerStarted = true;

    const startFinalizer = () => {
      window.__starsCarsPackageFinalizerPromise = finalizePendingPackage().catch((error) => {
        console.error("[Stars & Cars]", error);
      });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", startFinalizer, { once: true });
    } else {
      startFinalizer();
    }
  }

  if (customElements.get("cart-package-remove-button")) return;

  class CartPackageRemoveButton extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      this.addEventListener("click", this.handleClick.bind(this));
    }

    get lineKeys() {
      try {
        const keys = JSON.parse(this.dataset.lineKeys || "[]");
        return Array.isArray(keys) ? keys.filter(Boolean) : [];
      } catch (_error) {
        return [];
      }
    }

    get errorElement() {
      return this.closest(".stars-cars-package")?.querySelector(
        "[data-package-error] .cart-item-error-text",
      );
    }

    setError(message) {
      if (this.errorElement) this.errorElement.textContent = message;
    }

    async handleClick(event) {
      event.preventDefault();

      const lineKeys = this.lineKeys;
      if (!lineKeys.length || this.getAttribute("aria-busy") === "true") return;

      const cartContext = this.closest("cart-items, mini-cart");
      const sectionDescriptors = cartContext?.getSectionsToRender?.() || [];
      const sectionIds = [
        ...new Set(sectionDescriptors.map((section) => section.section).filter(Boolean)),
      ].slice(0, 5);
      const updates = Object.fromEntries(lineKeys.map((key) => [key, 0]));

      this.setAttribute("aria-busy", "true");
      this.querySelector("button")?.setAttribute("disabled", "disabled");
      this.setError("");
      cartContext?.enableLoading?.();

      try {
        const state = await fetchJson(`${shopRoot()}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

        if (!cartContext || !state.sections) {
          window.location.reload();
          return;
        }

        cartContext.classList.toggle("is-empty", state.item_count === 0);
        document
          .getElementById("main-cart-footer")
          ?.classList.toggle("is-empty", state.item_count === 0);

        sectionDescriptors.forEach((section) => {
          const sectionHtml = state.sections[section.section];
          const container = document.getElementById(section.id);
          if (!sectionHtml || !container) return;

          const destination = section.selector
            ? container.querySelector(section.selector) || container
            : container;
          destination.innerHTML = cartContext.getSectionInnerHTML(
            sectionHtml,
            section.selector,
          );
        });

        document.dispatchEvent(
          new CustomEvent("stars-cars:package-removed", {
            bubbles: true,
            detail: { cart: state },
          }),
        );
      } catch (error) {
        this.setError(error?.message || "We couldn't update your cart. Please try again.");
      } finally {
        this.removeAttribute("aria-busy");
        this.querySelector("button")?.removeAttribute("disabled");
        cartContext?.disableLoading?.();
      }
    }
  }

  customElements.define("cart-package-remove-button", CartPackageRemoveButton);
})();
